/**
 * Phase G.6 — /admin/mishap-analyses admin listing.
 * Mirrors AdminPlans styling. Admin-only via AdminShell guard.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import MishapResultsView from "@/components/MishapResultsView";

const PROGRAMS = [
  { slug: "", label: "All programs" },
  { slug: "uf-dietetics", label: "UF Dietetics" },
  { slug: "uf-business", label: "UF Business" },
  { slug: "uf-prehealth", label: "UF Pre-Health" },
  { slug: "ucf-business", label: "UCF Business" },
  { slug: "ucf-prehealth", label: "UCF Pre-Health" },
];

const SEVERITIES = ["", "critical", "high", "medium", "low"];

function SevChip({ n, sev }) {
  if (!n) return null;
  const cls = {
    critical: "bg-crimson text-bone",
    high: "bg-brass text-ivy",
    medium: "bg-ivy text-bone",
    low: "bg-bone-dim text-ink-soft border border-stone/40",
  }[sev];
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold ${cls}`} data-testid={`admin-mishap-sev-${sev}`}>
      {n} {sev.slice(0, 3)}
    </span>
  );
}

export default function AdminMishapAnalyses() {
  const [rows, setRows] = useState([]);
  const [programSlug, setProgramSlug] = useState("");
  const [severity, setSeverity] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = {};
    if (programSlug) params.program_slug = programSlug;
    if (severity) params.severity = severity;
    api.get("/admin/mishap/analyses", { params }).then((r) => setRows(r.data || []));
  };
  useEffect(() => { load(); }, [programSlug, severity]);

  const countSev = (mishaps, sev) => (mishaps || []).filter((m) => m.severity === sev).length;
  const programLabel = (slug) => PROGRAMS.find((p) => p.slug === slug)?.label?.replace(/^UCF |^UF /, "") || slug;

  return (
    <div data-testid="admin-mishap-page">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-ivy tracking-tight">Mishap analyses</h1>
          <p className="text-sm text-stone mt-1">{rows.length} analyses</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={programSlug}
            onChange={(e) => setProgramSlug(e.target.value)}
            className="border border-stone/40 bg-bone px-3 py-1.5 text-sm"
            data-testid="admin-mishap-program-filter"
          >
            {PROGRAMS.map((p) => <option key={p.slug || "all"} value={p.slug}>{p.label}</option>)}
          </select>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="border border-stone/40 bg-bone px-3 py-1.5 text-sm"
            data-testid="admin-mishap-severity-filter"
          >
            {SEVERITIES.map((s) => <option key={s || "all"} value={s}>{s ? `has ${s}` : "Any severity"}</option>)}
          </select>
        </div>
      </div>

      <div className="border border-stone/30 bg-bone">
        <table className="w-full text-sm">
          <thead className="bg-bone-dim/40 text-[10px] uppercase tracking-[0.18em] text-brass">
            <tr>
              <th className="text-left px-4 py-2.5">Created</th>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Program</th>
              <th className="text-left px-4 py-2.5">Severity</th>
              <th className="text-right px-4 py-2.5">Extra sem</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-stone px-4 py-10 italic">No analyses match.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.analysis_id} className="border-t border-stone/20 hover:bg-bone-dim/20" data-testid={`admin-mishap-row-${r.analysis_id}`}>
                <td className="px-4 py-2.5 text-stone text-xs tabular-nums">{(r.generated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5 text-ink">{r.first_name || "—"}</td>
                <td className="px-4 py-2.5 text-ink-soft text-xs">{r.email || "—"}</td>
                <td className="px-4 py-2.5 text-[11px] text-ivy font-semibold">{programLabel(r.program_slug)}</td>
                <td className="px-4 py-2.5 space-x-1">
                  <SevChip n={countSev(r.mishaps, "critical")} sev="critical" />
                  <SevChip n={countSev(r.mishaps, "high")} sev="high" />
                  <SevChip n={countSev(r.mishaps, "medium")} sev="medium" />
                  <SevChip n={countSev(r.mishaps, "low")} sev="low" />
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-ink">{r.extra_semesters_added ?? 0}</td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => setDetail(r)}
                    className="text-[11px] uppercase tracking-[0.18em] text-ivy hover:text-brass"
                    data-testid={`admin-mishap-open-${r.analysis_id}`}
                  >View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-start justify-center py-10 px-4 overflow-y-auto" onClick={() => setDetail(null)} data-testid="admin-mishap-detail">
          <div className="bg-bone border border-stone/40 w-full max-w-4xl p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Analysis · {detail.analysis_id.slice(-8)}</div>
                <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">{detail.first_name || "Analysis"} · {detail.email}</h2>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`/mishap-corrector/results/${detail.analysis_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] uppercase tracking-[0.18em] text-ivy hover:text-brass"
                  data-testid="admin-mishap-public-link"
                >Public link →</a>
                <button onClick={() => setDetail(null)} className="text-stone hover:text-ink text-2xl">×</button>
              </div>
            </div>
            <MishapResultsView analysis={detail} showPrintCTA={false} />
          </div>
        </div>
      )}
    </div>
  );
}
